
import { useEffect } from 'react';
import { getCanonicalUrl, getCurrentCanonicalUrl } from '@/utils/seoUtils';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string; // Open Graph image URL
  ogType?: string;
  noIndex?: boolean;
  disabled?: boolean;
  schema?: Record<string, unknown>; // Add schema support
}

/**
 * Custom hook for managing page metadata without using react-helmet-async
 * Modified to be SSR-compatible by checking for browser environment
 * 
 * @param title Page title
 * @param description Meta description
 * @param canonicalUrl Optional canonical URL
 * @param schema Optional JSON-LD schema data
 */
export const useSEO = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  noIndex = false,
  disabled = false,
  schema,
}: SEOProps) => {
  useEffect(() => {
    if (disabled) {
      return;
    }

    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // Ensure document and document.head exist (iOS safeguard)
    if (!document || !document.head) {
      console.warn('useSEO: document.head not ready');
      return;
    }

    try {
      // Set page title
      if (title) {
        document.title = title;
      }

      // Set meta description
      try {
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute("content", description);
        } else {
          const newMetaDescription = document.createElement('meta');
          newMetaDescription.name = "description";
          newMetaDescription.content = description;
          document.head.appendChild(newMetaDescription);
        }
      } catch (err) {
        console.error('Error setting meta description:', err);
      }

      // Sync og:title and og:description with page title/description
      try {
        let ogTypeMeta = document.querySelector('meta[property="og:type"]');
        if (!ogTypeMeta) {
          ogTypeMeta = document.createElement('meta');
          ogTypeMeta.setAttribute('property', 'og:type');
          document.head.appendChild(ogTypeMeta);
        }
        ogTypeMeta.setAttribute('content', ogType);

        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
          ogTitle.setAttribute("content", title);
        } else {
          const newOgTitle = document.createElement('meta');
          newOgTitle.setAttribute("property", "og:title");
          newOgTitle.setAttribute("content", title);
          document.head.appendChild(newOgTitle);
        }

        const ogDesc = document.querySelector('meta[property="og:description"]');
        if (ogDesc) {
          ogDesc.setAttribute("content", description);
        } else {
          const newOgDesc = document.createElement('meta');
          newOgDesc.setAttribute("property", "og:description");
          newOgDesc.setAttribute("content", description);
          document.head.appendChild(newOgDesc);
        }

        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (twitterTitle) {
          twitterTitle.setAttribute("content", title);
        } else {
          const newTwitterTitle = document.createElement('meta');
          newTwitterTitle.setAttribute("name", "twitter:title");
          newTwitterTitle.setAttribute("content", title);
          document.head.appendChild(newTwitterTitle);
        }

        const twitterDesc = document.querySelector('meta[name="twitter:description"]');
        if (twitterDesc) {
          twitterDesc.setAttribute("content", description);
        } else {
          const newTwitterDesc = document.createElement('meta');
          newTwitterDesc.setAttribute("name", "twitter:description");
          newTwitterDesc.setAttribute("content", description);
          document.head.appendChild(newTwitterDesc);
        }
      } catch (err) {
        console.error('Error setting OG/Twitter meta:', err);
      }

      // Set canonical URL - use provided or generate from current path
      try {
        const finalCanonicalUrl = canonicalUrl || (!noIndex ? getCurrentCanonicalUrl() : undefined);

        let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (finalCanonicalUrl) {
          if (canonicalLink) {
            canonicalLink.href = finalCanonicalUrl;
          } else {
            canonicalLink = document.createElement('link') as HTMLLinkElement;
            canonicalLink.rel = "canonical";
            canonicalLink.href = finalCanonicalUrl;
            document.head.appendChild(canonicalLink);
          }
        } else if (canonicalLink) {
          canonicalLink.remove();
        }

        // Also update og:url to match canonical URL (critical for SEO)
        const ogUrlMeta = document.querySelector('meta[property="og:url"]');
        if (finalCanonicalUrl && ogUrlMeta) {
          ogUrlMeta.setAttribute("content", finalCanonicalUrl);
        } else if (finalCanonicalUrl) {
          const newOgUrl = document.createElement('meta');
          newOgUrl.setAttribute("property", "og:url");
          newOgUrl.setAttribute("content", finalCanonicalUrl);
          document.head.appendChild(newOgUrl);
        } else if (ogUrlMeta) {
          ogUrlMeta.remove();
        }
      } catch (err) {
        console.error('Error setting canonical URL:', err);
      }

      try {
        const robotsContent = noIndex
          ? 'noindex, nofollow'
          : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

        const robotsMeta = document.querySelector('meta[name="robots"]');
        if (robotsMeta) {
          robotsMeta.setAttribute('content', robotsContent);
        } else {
          const newRobotsMeta = document.createElement('meta');
          newRobotsMeta.setAttribute('name', 'robots');
          newRobotsMeta.setAttribute('content', robotsContent);
          document.head.appendChild(newRobotsMeta);
        }
      } catch (err) {
        console.error('Error setting robots meta:', err);
      }

      // Set og:image if provided
      if (ogImage) {
        try {
          const ogImageMeta = document.querySelector('meta[property="og:image"]');
          if (ogImageMeta) {
            ogImageMeta.setAttribute("content", ogImage);
          } else {
            const newOgImage = document.createElement('meta');
            newOgImage.setAttribute("property", "og:image");
            newOgImage.setAttribute("content", ogImage);
            document.head.appendChild(newOgImage);
          }

          // Also update Twitter image
          const twitterImage = document.querySelector('meta[name="twitter:image"]');
          if (twitterImage) {
            twitterImage.setAttribute("content", ogImage);
          }
        } catch (err) {
          console.error('Error setting og:image:', err);
        }
      }

      // Replace the app-owned SSR schema on hydration and navigation. Update
      // synchronously so an old route's idle callback cannot overwrite this page.
      // A page without schema must also clear the previous route's structured data.
      try {
        const schemaString = schema ? JSON.stringify(schema) : undefined;
        const existingSchemas = document.querySelectorAll(
          'script[type="application/ld+json"][data-dynamic-schema], script[type="application/ld+json"][data-ssr-schema]',
        );
        existingSchemas.forEach(script => script.remove());

        if (schemaString) {
          const schemaScript = document.createElement('script');
          schemaScript.type = 'application/ld+json';
          schemaScript.setAttribute('data-dynamic-schema', 'true');
          schemaScript.textContent = schemaString;
          document.head.appendChild(schemaScript);
        }
      } catch (error) {
        console.error('Error updating schema:', error);
      }
    } catch (error) {
      console.error('Error in useSEO hook:', error);
      // Log additional context for iOS debugging
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        console.error('iOS device detected - SEO error context:', {
          title,
          hasSchema: !!schema,
          userAgent: navigator.userAgent
        });
      }
      // Don't break the page if SEO updates fail
    }

    // Cleanup function to remove any added elements if needed
    return () => {
      // If we wanted to revert changes, we could do it here
      // But generally we don't want to remove metadata when a component unmounts
    };
  }, [title, description, canonicalUrl, ogImage, ogType, noIndex, disabled, schema]);
};

export default useSEO;
